import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { rememberDentist, rememberedDentist, storedDrafts, storeDrafts } from '@/lib/review-memory';
import { TooltipProvider } from '@/shared/ui/tooltip';
import type {
  CaseSummary,
  DecisionInput,
  DecisionView,
  DentistDetails,
  DraftNote,
  NoteView,
  PatientLinkRequest,
  PatientLinkView,
  SceneManifest,
  ViewState,
} from '@/types';
import type { ReviewViewerHandle } from '@/components/features/viewer/ReviewViewer';
import type {
  HeatmapMode,
  HeatmapStat,
  HeatmapStatus,
  Measurement,
  PinSpec,
  SectionProfile,
  SectionReading,
  SectionState,
  ViewerTool,
} from '@/components/features/viewer/engine/types';
import { HeatmapLegend } from '@/components/features/viewer/overlays/HeatmapLegend';
import { LayersPanel } from '@/components/features/viewer/overlays/LayersPanel';
import { NavPad } from '@/components/features/viewer/overlays/NavPad';
import { DraggablePanel } from '@/components/features/viewer/overlays/DraggablePanel';
import { SectionControl } from '@/components/features/viewer/overlays/SectionControl';
import { ToolDock } from '@/components/features/viewer/overlays/ToolDock';
import { ViewPresets } from '@/components/features/viewer/overlays/ViewPresets';
import { DecisionDock } from './decision/DecisionDock';
import { DetailsPanel } from './details/DetailsPanel';
import { CaseHeader } from './header/CaseHeader';
import { overallStage } from '@/lib/review-stage';
import { NotesThread } from './notes/NotesThread';
import { DetailsBar } from './panel/DetailsBar';
import { SendDock } from './panel/SendDock';
import { SharePatientDialog } from './share/SharePatientDialog';

/**
 * The dentist's review, on one screen.
 *
 * The model takes the room; every control floats over it where the workbench
 * puts its own, so a technician and a dentist looking at the same case look at
 * the same layout. Notes and the case details share the side panel, and the
 * decision is docked beneath them - always visible, never in the way.
 *
 * Data comes in through props and changes go out through callbacks. This
 * component does not know whether it is talking to the live API or to the
 * local preview, which is what lets both run the exact same screen.
 */

const ReviewViewer = lazy(() =>
  import('@/components/features/viewer/ReviewViewer').then((module) => ({
    default: module.ReviewViewer,
  })),
);

/** The pin placed for the note being written, before Add comment. */
const PENDING_PIN_ID = 'pending-pin';

export interface ReviewWorkspaceProps {
  summary: CaseSummary;
  manifest: SceneManifest;
  notes: NoteView[];
  decision: DecisionView | null;
  /** The dentist's details from their last review for this lab, if any. */
  knownDentist: DentistDetails | null;
  /** Names this link's unsent notes in the browser, so a reload keeps them. */
  storageKey: string;
  /** Sends the decision with the round's notes. */
  onDecide: (decision: DecisionInput) => Promise<void>;
  /** The patient link, once made; its appointment answer is the patient's reply. */
  patientLink: PatientLinkView | null;
  /** Without these the header offers no patient link (the live link, until the server stores them). */
  onCreatePatientLink?: (request: PatientLinkRequest) => Promise<void>;
  onTurnOffPatientLink?: () => Promise<void>;
  /** Without it the header has no PDF report button. */
  onReport?: () => void;
}

type SideTab = 'notes' | 'details';

function initialOpacity(manifest: SceneManifest): Record<string, number> {
  return Object.fromEntries(manifest.layers.map((layer) => [layer.id, layer.opacity]));
}

/** Keys typed into a field are text, not shortcuts. */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

export function ReviewWorkspace({
  summary,
  manifest,
  notes,
  decision,
  knownDentist,
  storageKey,
  onDecide,
  patientLink,
  onCreatePatientLink,
  onTurnOffPatientLink,
  onReport,
}: ReviewWorkspaceProps) {
  const viewerRef = useRef<ReviewViewerHandle | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const teeth = useMemo(
    () =>
      manifest.layers
        .filter((layer) => layer.kind === 'crown' && layer.tooth)
        .map((layer) => layer.tooth!),
    [manifest],
  );

  const [opacity, setOpacity] = useState(() => initialOpacity(manifest));
  const [showMargins, setShowMargins] = useState(true);
  const [tool, setTool] = useState<ViewerTool>('rotate');
  const [section, setSection] = useState<SectionState>({
    enabled: false,
    tooth: teeth[0] ?? '',
    axis: 'buccolingual',
    position: 0,
    flipped: false,
  });
  const [sectionOffset, setSectionOffset] = useState(0);
  const [sectionReading, setSectionReading] = useState<SectionReading | null>(null);
  const [sectionProfile, setSectionProfile] = useState<SectionProfile | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapMode>('off');
  const [heatmapStatus, setHeatmapStatus] = useState<HeatmapStatus>('idle');
  const [heatmapStats, setHeatmapStats] = useState<HeatmapStat[]>([]);
  const [walkingTooth, setWalkingTooth] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [focusedTooth, setFocusedTooth] = useState<string | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [flashNoteId, setFlashNoteId] = useState<string | null>(null);
  const [showPins, setShowPins] = useState(true);
  const [pending, setPending] = useState<{
    position: [number, number, number];
    view: ViewState;
  } | null>(null);
  // Who is reviewing: what the lab's NuCAD sent with the link first (it is
  // this link's dentist), then what this browser remembers from last time.
  const [remembered] = useState(rememberedDentist);
  const known = useMemo<DentistDetails | null>(
    () =>
      knownDentist?.name || knownDentist?.clinic
        ? {
            name: knownDentist.name || remembered?.name || '',
            clinic: knownDentist.clinic || remembered?.clinic || '',
            registrationNo: '',
          }
        : remembered
          ? { name: remembered.name, clinic: remembered.clinic, registrationNo: '' }
          : null,
    [remembered, knownDentist],
  );
  const [dentistName, setDentistName] = useState(known?.name ?? '');
  const [dentistClinic, setDentistClinic] = useState(known?.clinic ?? '');
  const authorName = dentistName;
  const [drafts, setDrafts] = useState<DraftNote[]>(() => storedDrafts(storageKey));
  useEffect(() => {
    storeDrafts(storageKey, drafts);
  }, [storageKey, drafts]);
  const [tab, setTab] = useState<SideTab>('notes');
  const [fullscreen, setFullscreen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const closed = decision !== null;
  // Comments go out under the dentist's name and clinic: until both are in,
  // the comment box (and pinning a comment) stays shut, and so does Send.
  const missingDetails = [
    dentistName.trim() ? null : 'name',
    dentistClinic.trim() ? null : 'clinic',
  ].filter(Boolean);
  const needDetails = missingDetails.length
    ? `Add your ${missingDetails.join(' and ')} at the top to comment.`
    : null;

  /*
   * The thread: what was sent, then the dentist's own unsent notes. Drafts
   * number their pins on from the sent ones, so the pin on the model and the
   * number in the list always agree.
   */
  const thread = useMemo<NoteView[]>(() => {
    if (closed) return notes;
    let pin = notes.reduce((top, note) => Math.max(top, note.pin ?? 0), 0);
    return [
      ...notes,
      ...drafts.map((draft): NoteView => ({
        id: draft.id,
        pin: draft.position ? ++pin : null,
        authorName: authorName || 'You',
        authorRole: 'dentist',
        text: draft.text,
        createdAt: draft.createdAt,
        position: draft.position,
        view: draft.view,
        stage: draft.stage,
        draft: true,
      })),
    ];
  }, [notes, drafts, closed, authorName]);

  const pins = useMemo<PinSpec[]>(() => {
    const placed = thread
      .filter((note) => note.position && note.pin !== null)
      .map((note) => ({
        id: note.id,
        number: note.pin!,
        position: note.position!,
        fromLab: note.authorRole === 'lab',
      }));
    if (!pending) return placed;
    // The spot just clicked shows at once, under the number its note will get,
    // rather than only once Add comment is pressed.
    const next = placed.reduce((top, pin) => Math.max(top, pin.number), 0) + 1;
    return [
      ...placed,
      { id: PENDING_PIN_ID, number: next, position: pending.position, pending: true },
    ];
  }, [thread, pending]);

  useEffect(() => {
    if (!flashNoteId) return;
    const timer = window.setTimeout(() => {
      setFlashNoteId(null);
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [flashNoteId]);

  const setLayer = useCallback((id: string, value: number) => {
    setOpacity((was) => ({ ...was, [id]: value }));
  }, []);

  /** Puts the dentist where a note was written: same angle, same layers. */
  const showNote = useCallback((note: NoteView) => {
    setActiveNoteId(note.id);
    setTab('notes');
    if (note.view) {
      viewerRef.current?.showCamera(note.view.camera);
      setOpacity((was) => ({ ...was, ...note.view!.layers }));
    }
  }, []);

  const handlePick = useCallback(
    (position: [number, number, number], camera: ViewState['camera']) => {
      setPending({ position, view: { camera, layers: opacity } });
      setTool('rotate');
      setTab('notes');
    },
    [opacity],
  );

  const toggleSection = useCallback(() => {
    // On: following the cursor, as NuCAD's Cut View starts.
    setSection((was) => ({
      ...was,
      enabled: !was.enabled,
      locked: false,
      tooth: focusedTooth ?? was.tooth,
    }));
  }, [focusedTooth]);

  /*
   * The previous version's crowns, when the case has them. Comparing ghosts
   * them in blue and paints the current crowns by what moved; any other map
   * puts the ghosts away again.
   */
  const previousIds = useMemo(
    () => manifest.layers.filter((layer) => layer.kind === 'previous').map((layer) => layer.id),
    [manifest],
  );
  const previousVersion = previousIds.length > 0 ? summary.version - 1 : null;
  const minThickness =
    summary.parameters.find(
      (parameter) => parameter.key === 'min_thickness' || parameter.key === 'min_wall_thickness',
    )?.value ?? 0.6;

  const chooseMap = useCallback(
    (mode: HeatmapMode) => {
      setHeatmap(mode);
      setOpacity((was) => ({
        ...was,
        ...Object.fromEntries(previousIds.map((id) => [id, mode === 'changes' ? 0.2 : 0])),
      }));
    },
    [previousIds],
  );

  const toggleHeatmap = useCallback(() => {
    chooseMap(heatmap === 'off' || heatmap === 'changes' ? 'occlusal' : 'off');
  }, [chooseMap, heatmap]);

  const toggleCompare = useCallback(() => {
    if (previousVersion === null) return;
    chooseMap(heatmap === 'changes' ? 'off' : 'changes');
  }, [chooseMap, heatmap, previousVersion]);

  /*
   * During the walk the opposite jaw goes and the tooth's own jaw turns
   * see-through: the margin sits at the gum, under both of them. What the
   * dentist had set is put back when the walk ends.
   */
  const opacityBeforeWalk = useRef<Record<string, number> | null>(null);

  const toggleMarginWalk = useCallback(() => {
    if (walkingTooth) {
      viewerRef.current?.stopMarginWalk();
      return;
    }
    const tooth = focusedTooth ?? teeth[0];
    if (!tooth) return;
    if (!viewerRef.current?.startMarginWalk(tooth)) {
      toast.info(`No margin line for tooth ${tooth} in this design.`);
      return;
    }
    const upperTooth = tooth.startsWith('1') || tooth.startsWith('2');
    opacityBeforeWalk.current = opacity;
    setOpacity((was) => ({
      ...was,
      [upperTooth ? 'lower' : 'upper']: 0,
      [upperTooth ? 'upper' : 'lower']: 0.45,
    }));
    setShowMargins(true);
    setWalkingTooth(tooth);
  }, [walkingTooth, focusedTooth, teeth, opacity]);

  const endMarginWalk = useCallback(() => {
    setWalkingTooth(null);
    if (opacityBeforeWalk.current) setOpacity(opacityBeforeWalk.current);
    opacityBeforeWalk.current = null;
  }, []);

  const screenshot = useCallback(() => {
    const data = viewerRef.current?.screenshot();
    if (!data) return;
    const anchor = document.createElement('a');
    anchor.href = data;
    anchor.download = `crown-review-${summary.patientRef}-v${String(summary.version)}.png`;
    anchor.click();
  }, [summary.patientRef, summary.version]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stageRef.current?.requestFullscreen();
  }, []);

  useEffect(() => {
    const onChange = () => {
      setFullscreen(document.fullscreenElement === stageRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  // The workbench's single-key shortcuts, for the dentist who knows them.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 'r') setTool('rotate');
      else if (key === 'p') setTool('pan');
      else if (key === 'n' && !closed && !needDetails) setTool('pin');
      else if (key === 'm') setTool('measure');
      else if (key === 's') toggleSection();
      else if (key === 'h') toggleHeatmap();
      else if (key === 'w') toggleMarginWalk();
      else if (key === 'c') toggleCompare();
      else if (key === 'f') viewerRef.current?.frameCrowns();
      else if (key === 'escape') setTool('rotate');
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [closed, needDetails, toggleSection, toggleHeatmap, toggleMarginWalk, toggleCompare]);

  const noteCount = thread.length;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-h-dvh flex-col bg-background text-foreground lg:h-dvh">
        <CaseHeader
          summary={summary}
          patientLink={patientLink}
          {...(onCreatePatientLink
            ? {
                onSharePatient: () => {
                  setShareOpen(true);
                },
              }
            : {})}
          {...(onReport ? { onReport } : {})}
        />

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* ── The stage ─────────────────────────────────────────────── */}
          <div
            ref={stageRef}
            className="stage-ground relative h-[68vh] shrink-0 overflow-hidden lg:h-auto lg:flex-1"
          >
            <Suspense fallback={null}>
              <ReviewViewer
                ref={viewerRef}
                manifest={manifest}
                opacity={opacity}
                showMargins={showMargins}
                tool={tool}
                section={section}
                heatmap={heatmap}
                minThickness={minThickness}
                pins={showPins ? pins : pins.filter((pin) => pin.pending)}
                activePinId={activeNoteId}
                className="absolute inset-0"
                onPick={handlePick}
                onPinClick={(id) => {
                  const note = thread.find((item) => item.id === id);
                  if (!note) return;
                  showNote(note);
                  // Re-set even if it is the same note, so a second click
                  // flashes it again.
                  setFlashNoteId(null);
                  window.requestAnimationFrame(() => {
                    setFlashNoteId(note.id);
                  });
                }}
                onMeasurementsChange={setMeasurements}
                onHeatmapStatus={setHeatmapStatus}
                onHeatmapStats={setHeatmapStats}
                onMarginWalkEnd={endMarginWalk}
                onSectionOffset={setSectionOffset}
                onSectionReading={setSectionReading}
                onSectionProfile={setSectionProfile}
                onSectionHover={(position) => {
                  setSection((was) => (was.locked ? was : { ...was, position }));
                }}
                onSectionClick={() => {
                  setSection((was) => ({ ...was, locked: !was.locked }));
                }}
              />
            </Suspense>

            <div className="pointer-events-none absolute inset-0 p-3 *:pointer-events-auto">
              <div className="absolute top-3 left-3">
                <ViewPresets
                  teeth={teeth}
                  focusedTooth={focusedTooth}
                  onView={(preset) => viewerRef.current?.setView(preset)}
                  onFocusTooth={(tooth) => {
                    setFocusedTooth(tooth);
                    if (tooth) {
                      viewerRef.current?.focusTooth(tooth);
                      setSection((was) => ({ ...was, tooth, position: 0 }));
                    } else {
                      viewerRef.current?.frameCrowns();
                    }
                  }}
                />
              </div>

              {walkingTooth && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-3 rounded-full bg-neutral-900/85 py-1.5 pr-1.5 pl-4 text-[12.5px] text-white shadow-lg backdrop-blur">
                    Walking the margin of {walkingTooth}
                    <button
                      type="button"
                      onClick={() => viewerRef.current?.stopMarginWalk()}
                      className="rounded-full bg-white/15 px-3 py-1 font-medium hover:bg-white/25"
                    >
                      Stop
                    </button>
                  </div>
                </div>
              )}

              <div className="absolute top-3 right-3 hidden md:block">
                <LayersPanel
                  layers={manifest.layers.filter(
                    (layer) => layer.kind !== 'previous' || heatmap === 'changes',
                  )}
                  opacity={opacity}
                  onOpacity={setLayer}
                  showMargins={showMargins}
                  onShowMargins={setShowMargins}
                />
              </div>

              {/*
                The cut opens on the left under the view buttons, off the
                model, and the dentist can drag it anywhere by its header.
              */}
              {section.enabled && (
                <DraggablePanel storageKey="cut-view" defaultClassName="top-16 left-3">
                  <SectionControl
                    section={section}
                    teeth={teeth}
                    offsetMm={sectionOffset}
                    reading={sectionReading}
                    minThickness={minThickness}
                    profile={sectionProfile}
                    onChange={setSection}
                  />
                </DraggablePanel>
              )}

              {/*
                The map key sits at the bottom centre, just above the toolbar
                row so it never sits on the tools.
              */}
              {heatmap !== 'off' && (
                <div className="absolute inset-x-3 bottom-16 flex flex-col items-center gap-2 xl:inset-x-auto xl:left-1/2 xl:-translate-x-1/2">
                  <HeatmapLegend
                    mode={heatmap}
                    status={heatmapStatus}
                    stats={heatmapStats}
                    minThickness={minThickness}
                    previousVersion={previousVersion}
                    onMode={chooseMap}
                  />
                </div>
              )}

              <div className="absolute bottom-3 left-3">
                <ToolDock
                  tool={tool}
                  onTool={(next) => {
                    // A pin is the start of a comment, so it waits for the
                    // name and clinic too - said, rather than a dead button.
                    if (next === 'pin' && needDetails) {
                      toast.info(needDetails);
                      return;
                    }
                    setTool(next);
                  }}
                  sectionOn={section.enabled}
                  onSection={toggleSection}
                  heatmapOn={heatmap !== 'off' && heatmap !== 'changes'}
                  onHeatmap={toggleHeatmap}
                  previousVersion={previousVersion}
                  compareOn={heatmap === 'changes'}
                  onCompare={toggleCompare}
                  walkingMargin={walkingTooth !== null}
                  onMarginWalk={toggleMarginWalk}
                  onScreenshot={screenshot}
                  measurements={measurements}
                  onClearMeasurements={() => viewerRef.current?.clearMeasurements()}
                  canPin={!closed}
                />
              </div>

              <div className="absolute right-3 bottom-3">
                <NavPad
                  onZoomIn={() => viewerRef.current?.zoom(0.75)}
                  onZoomOut={() => viewerRef.current?.zoom(1.33)}
                  onFrameCrowns={() => viewerRef.current?.frameCrowns()}
                  onFrameAll={() => viewerRef.current?.frameAll()}
                  fullscreen={fullscreen}
                  onFullscreen={toggleFullscreen}
                />
              </div>
            </div>
          </div>

          {/* ── The side panel ────────────────────────────────────────── */}
          <aside className="flex min-h-[70vh] w-full flex-col border-t border-border bg-background lg:min-h-0 lg:w-[400px] lg:border-t-0 lg:border-l">
            <DetailsBar
              name={decision ? decision.deciderName : dentistName}
              clinic={decision ? decision.clinic : dentistClinic}
              onName={setDentistName}
              onClinic={setDentistClinic}
              readOnly={closed}
            />
            <div
              role="tablist"
              aria-label="Review panel"
              className="flex border-b border-border px-2"
            >
              {(
                [
                  ['notes', `Notes${noteCount ? ` · ${String(noteCount)}` : ''}`],
                  ['details', 'Case details'],
                ] as [SideTab, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={tab === value}
                  onClick={() => {
                    setTab(value);
                  }}
                  className={cn(
                    '-mb-px border-b-2 px-3 py-3 text-[13px] font-semibold transition-colors',
                    tab === value
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'notes' ? (
              <NotesThread
                notes={thread}
                activeId={activeNoteId}
                flashId={flashNoteId}
                showPins={showPins}
                onShowPins={setShowPins}
                onActivate={showNote}
                pending={pending}
                onClearPending={() => {
                  setPending(null);
                }}
                pinning={tool === 'pin'}
                onPin={() => {
                  if (!needDetails) setTool((was) => (was === 'pin' ? 'rotate' : 'pin'));
                }}
                onAddDraft={(note) => {
                  setDrafts((was) => [
                    ...was,
                    {
                      ...note,
                      id: `draft-${crypto.randomUUID()}`,
                      createdAt: new Date().toISOString(),
                    },
                  ]);
                }}
                onRemoveDraft={(id) => {
                  setDrafts((was) => was.filter((draft) => draft.id !== id));
                }}
                closed={closed}
                needDetails={needDetails}
              />
            ) : (
              <DetailsPanel summary={summary} />
            )}

            {decision ? (
              <DecisionDock
                existing={decision}
                authorName={authorName}
                known={known}
                draftCount={0}
                onDecide={() => Promise.resolve()}
              />
            ) : (
              <SendDock
                fromComments={overallStage(drafts.map((draft) => draft.stage))}
                commentCount={drafts.length}
                blocked={needDetails ? needDetails.replace('to comment', 'to send') : null}
                onSend={async (status) => {
                  await onDecide({
                    decision: status,
                    deciderName: dentistName.trim(),
                    clinic: dentistClinic.trim(),
                    registrationNo: '',
                    note: '',
                    notes: drafts.map((draft) => ({
                      text: draft.text,
                      stage: draft.stage,
                      position: draft.position,
                      view: draft.view,
                    })),
                  });
                  rememberDentist({
                    name: dentistName,
                    clinic: dentistClinic,
                    registrationNo: '',
                    email: remembered?.email ?? '',
                  });
                  const sent = drafts.length;
                  setDrafts([]);
                  toast.success(
                    sent
                      ? `Sent to the technician with ${String(sent)} comment${sent === 1 ? '' : 's'}`
                      : 'Sent to the technician',
                  );
                }}
              />
            )}
          </aside>
        </div>
      </div>

      {onCreatePatientLink && onTurnOffPatientLink && (
        <SharePatientDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          patientLink={patientLink}
          onCreate={onCreatePatientLink}
          onTurnOff={onTurnOffPatientLink}
        />
      )}
    </TooltipProvider>
  );
}
